<template>
  <div v-if="hasMultipleOrgs" ref="rootRef" class="org-context-picker">
    <UnitButton
      class="org-context-trigger"
      variant="secondary"
      full-width
      :aria-expanded="String(open)"
      aria-haspopup="listbox"
      :aria-controls="listboxId"
      @click="toggle"
      @keydown.down.prevent="openAndFocusFirst"
      @keydown.escape.prevent="close"
    >
      <span class="org-context-trigger-label">{{ selectedLabel }}</span>
      <template #trailing>
        <svg class="tinyImg" aria-hidden="true">
          <use href="@/assets/icons/iconset.svg#arrowdown_carousel-tiny" />
        </svg>
      </template>
    </UnitButton>

    <div v-if="open" class="org-context-panel">
      <label v-if="showSearch" :for="searchId" class="org-context-visually-hidden">
        Search organisations
      </label>
      <input
        v-if="showSearch"
        :id="searchId"
        ref="searchRef"
        v-model.trim="search"
        class="unit-field smallInput org-context-search"
        type="search"
        autocomplete="off"
        placeholder="Search organisations…"
        @keydown.escape.prevent="closeAndRestoreFocus"
      />

      <p
        v-if="remoteLoading || remoteError"
        class="org-context-status"
        :role="remoteError ? 'alert' : 'status'"
        aria-live="polite"
      >
        {{ remoteError || "Loading organisations…" }}
      </p>
      <p
        v-if="selectionError"
        class="org-context-status org-context-status-error"
        role="alert"
        aria-live="assertive"
      >
        {{ selectionError }}
      </p>

      <div :id="listboxId" class="org-context-list" role="listbox" aria-label="Organisation">
        <template v-if="!search && recentOptions.length">
          <p class="org-context-section-label">Recent</p>
          <UnitButton
            v-for="option in recentOptions"
            :key="`recent-${option.id}`"
            role="option"
            variant="text"
            full-width
            class="org-context-option"
            :aria-selected="String(option.id === selectedOrgUID)"
            @click="select(option.id)"
            @keydown="onOptionKeydown"
          >
            {{ option.label }}
          </UnitButton>
          <p class="org-context-section-label">All organisations</p>
        </template>

        <UnitButton
          v-for="option in visibleOptions"
          :key="option.id"
          role="option"
          variant="text"
          full-width
          class="org-context-option"
          :class="{ selected: option.id === selectedOrgUID }"
          :aria-selected="String(option.id === selectedOrgUID)"
          @click="select(option.id)"
          @keydown="onOptionKeydown"
        >
          {{ option.label }}
        </UnitButton>
        <p v-if="visibleOptions.length === 0" class="org-context-empty">
          No matching organisations
        </p>
      </div>

      <UnitButton
        v-if="canLoadMore && !remoteLoading"
        class="org-context-load-more"
        variant="text"
        full-width
        @click="loadMore"
      >
        Load more
      </UnitButton>
      <UnitButton
        v-if="remoteError"
        class="org-context-load-more"
        variant="text"
        full-width
        @click="loadRemote({ reset: true })"
      >
        Retry
      </UnitButton>
    </div>
    <p class="org-context-live" role="status" aria-live="polite">
      {{ liveMessage }}
    </p>
  </div>
</template>

<script>
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";
import { useStore } from "vuex";
import { isOrgContextFeatureEnabled } from "@/config/runtimeConfig";
import { organisationContextService } from "@/services/organisationContextService";
import UnitButton from "@/components/GenericComponents/UnitButton.vue";

let pickerSequence = 0;
const PAGE_SIZE = 10;

export default {
  name: "OrgContextPicker",
  components: { UnitButton },
  emits: ["change"],
  setup(_props, { emit }) {
    const store = useStore();
    const instanceId = `org-context-picker-${++pickerSequence}`;
    const listboxId = `${instanceId}-listbox`;
    const searchId = `${instanceId}-search`;
    const rootRef = ref(null);
    const searchRef = ref(null);
    const open = ref(false);
    const search = ref("");
    const liveMessage = ref("");
    const visibleCount = ref(PAGE_SIZE);
    const remoteContexts = ref([]);
    const remoteRecentContexts = ref([]);
    const remoteCursor = ref(null);
    const remoteHasMore = ref(false);
    const remoteLoaded = ref(false);
    const remoteLoading = ref(false);
    const remoteError = ref("");
    const remoteQuery = ref("");
    const selectionError = ref("");
    let remoteGeneration = 0;
    let searchTimer = null;
    const currentUserUID = computed(
      () => store.getters.userCredentials?.data?.uid || null,
    );
    const selectedOrgUID = computed(() => store.getters.selectedOrgUID);
    const memberships = computed(() => store.getters.userUnitsData || {});
    const hasMultipleOrgs = computed(() => store.getters.hasMultipleOrgs);
    const localOptions = computed(() =>
      Object.entries(memberships.value)
        .filter(
          ([, membership]) =>
            membership?.isActiveMember !== false &&
            membership?.membershipStatus !== "inactive",
        )
        .map(([id, membership]) => ({
          id,
          label: membership?.organisationName || membership?.name || id,
        }))
        .sort((left, right) => left.label.localeCompare(right.label)),
    );
    const options = computed(() =>
      isOrgContextFeatureEnabled && remoteLoaded.value
        ? remoteContexts.value
        : localOptions.value,
    );
    const selectedLabel = computed(() => {
      const labelSources = isOrgContextFeatureEnabled && remoteLoaded.value
        ? [
            ...remoteContexts.value,
            ...remoteRecentContexts.value,
            ...localOptions.value,
          ]
        : localOptions.value;
      return (
        labelSources.find((option) => option.id === selectedOrgUID.value)
          ?.label || "Organisation"
      );
    });
    const showSearch = computed(
      () =>
        search.value.length > 0 ||
        options.value.length > PAGE_SIZE ||
        remoteHasMore.value,
    );
    const recentOptions = computed(() => {
      if (isOrgContextFeatureEnabled && remoteLoaded.value) {
        return remoteRecentContexts.value
          .filter((context) => context.membershipStatus !== "inactive")
          .slice(0, 5);
      }
      const recentIds = store.getters.recentOrgUIDs || [];
      return recentIds
        .map((id) => localOptions.value.find((option) => option.id === id))
        .filter(Boolean)
        .slice(0, 5);
    });
    const filteredOptions = computed(() => {
      const query = search.value.toLocaleLowerCase();
      if (!query) return options.value;
      return options.value.filter((option) => option.label.toLocaleLowerCase().includes(query));
    });
    const nonRecentOptions = computed(() => {
      if (search.value) return filteredOptions.value;
      const recentIds = new Set(recentOptions.value.map((option) => option.id));
      return filteredOptions.value.filter((option) => !recentIds.has(option.id));
    });
    const visibleOptions = computed(() => nonRecentOptions.value.slice(0, visibleCount.value));
    const canLoadMore = computed(() =>
      isOrgContextFeatureEnabled && remoteLoaded.value
        ? remoteHasMore.value
        : visibleOptions.value.length < nonRecentOptions.value.length,
    );

    const resetRemoteResults = () => {
      remoteContexts.value = [];
      remoteRecentContexts.value = [];
      remoteCursor.value = null;
      remoteHasMore.value = false;
      remoteLoaded.value = false;
      remoteError.value = "";
      remoteQuery.value = "";
    };

    const loadRemote = async ({ reset = false, query = search.value } = {}) => {
      if (!isOrgContextFeatureEnabled) return;
      const generation = ++remoteGeneration;
      const requestedUserUID = currentUserUID.value;
      if (!requestedUserUID) return;
      const requestedSearch = String(query || "");
      remoteLoading.value = true;
      remoteError.value = "";
      const cursor = reset ? null : remoteCursor.value;
      try {
        const result = await organisationContextService.listOwnOrganisationContexts({
          cursor,
          search: requestedSearch,
          recentOrgUIDs: store.getters.recentOrgUIDs || [],
        });
        if (
          generation !== remoteGeneration ||
          requestedUserUID !== currentUserUID.value
        ) {
          return;
        }
        const mapped = result.contexts
          .filter((context) => context.membershipStatus !== "inactive")
          .map((context) => ({
            id: context.orgUID,
            label: context.name,
            membershipStatus: context.membershipStatus,
          }));
        remoteContexts.value = reset
          ? mapped
          : [...remoteContexts.value, ...mapped].filter(
              (option, index, all) =>
                all.findIndex((candidate) => candidate.id === option.id) === index,
            );
        remoteRecentContexts.value = result.recentContexts
          .filter((context) => context.membershipStatus !== "inactive")
          .map((context) => ({
            id: context.orgUID,
            label: context.name,
            membershipStatus: context.membershipStatus,
          }));
        remoteCursor.value = result.nextCursor;
        remoteHasMore.value = result.hasMore;
        remoteLoaded.value = true;
        remoteQuery.value = requestedSearch;
        visibleCount.value = Math.max(PAGE_SIZE, remoteContexts.value.length);
      } catch (_error) {
        if (
          generation === remoteGeneration &&
          requestedUserUID === currentUserUID.value
        ) {
          remoteError.value = "Could not load organisations. Try again.";
        }
      } finally {
        if (
          generation === remoteGeneration &&
          requestedUserUID === currentUserUID.value
        ) {
          remoteLoading.value = false;
        }
      }
    };

    const close = () => {
      const wasFiltered = Boolean(search.value || remoteQuery.value);
      open.value = false;
      search.value = "";
      visibleCount.value = PAGE_SIZE;
      remoteGeneration += 1;
      remoteLoading.value = false;
      selectionError.value = "";
      if (searchTimer) {
        clearTimeout(searchTimer);
        searchTimer = null;
      }
      if (wasFiltered) resetRemoteResults();
      document.removeEventListener("pointerdown", onDocumentPointerDown);
    };
    const closeAndRestoreFocus = async () => {
      close();
      await nextTick();
      rootRef.value
        ?.querySelector("[aria-haspopup='listbox']")
        ?.focus();
    };
    const optionElements = () =>
      Array.from(
        rootRef.value?.querySelectorAll("[role='option']") || []
      );
    const focusFirstOption = async () => {
      await nextTick();
      optionElements()[0]?.focus();
    };
    const openAndFocusFirst = async () => {
      if (!open.value) await toggle();
      await focusFirstOption();
    };
    const onOptionKeydown = (event) => {
      const options = optionElements();
      const currentIndex = options.indexOf(event.currentTarget);
      if (currentIndex < 0) return;
      let nextIndex = currentIndex;
      if (event.key === "ArrowDown") nextIndex = (currentIndex + 1) % options.length;
      else if (event.key === "ArrowUp") {
        nextIndex = (currentIndex - 1 + options.length) % options.length;
      } else if (event.key === "Home") nextIndex = 0;
      else if (event.key === "End") nextIndex = options.length - 1;
      else if (event.key === "Escape") {
        event.preventDefault();
        void closeAndRestoreFocus();
        return;
      } else return;
      event.preventDefault();
      options[nextIndex]?.focus();
    };
    const onDocumentPointerDown = (event) => {
      if (open.value && rootRef.value && !rootRef.value.contains(event.target)) close();
    };
    const toggle = async () => {
      if (open.value) {
        close();
        return;
      }
      open.value = true;
      document.addEventListener("pointerdown", onDocumentPointerDown);
      if (isOrgContextFeatureEnabled && !remoteLoaded.value) {
        loadRemote({ reset: true, query: "" });
      }
      if (showSearch.value) {
        await nextTick();
        searchRef.value?.focus();
      }
    };
    const select = async (orgUID) => {
      const userUID = store.getters.userCredentials?.data?.uid;
      selectionError.value = "";
      if (!userUID) {
        selectionError.value =
          "Could not change organisation. Sign in again and retry.";
        return;
      }
      const nextLabel =
        options.value.find((option) => option.id === orgUID)?.label ||
        "Organisation";
      try {
        await store.dispatch("setSelectedOrgUID", { userUID, orgUID });
        if (currentUserUID.value !== userUID) return;
        emit("change", orgUID);
        close();
        liveMessage.value = `Acting organisation changed to ${nextLabel}.`;
      } catch (_error) {
        selectionError.value = "Could not change organisation. Try again.";
      }
    };
    const loadMore = () => {
      if (isOrgContextFeatureEnabled && remoteLoaded.value) {
        loadRemote();
      } else {
        visibleCount.value += PAGE_SIZE;
      }
    };
    watch(search, (nextSearch) => {
      if (!isOrgContextFeatureEnabled || !open.value) return;
      // Invalidate any response for the previous query immediately. The new
      // debounced request may overlap it and its generation owns the result.
      remoteGeneration += 1;
      if (searchTimer) clearTimeout(searchTimer);
      searchTimer = null;
      if (!nextSearch) {
        resetRemoteResults();
        loadRemote({ reset: true, query: "" });
        return;
      }
      searchTimer = setTimeout(
        () => loadRemote({ reset: true, query: nextSearch }),
        250,
      );
    });
    watch(currentUserUID, () => {
      remoteGeneration += 1;
      remoteLoading.value = false;
      selectionError.value = "";
      liveMessage.value = "";
      close();
      resetRemoteResults();
    });
    onBeforeUnmount(() => {
      remoteGeneration += 1;
      if (searchTimer) clearTimeout(searchTimer);
      document.removeEventListener("pointerdown", onDocumentPointerDown);
    });

    return {
      PAGE_SIZE,
      listboxId,
      searchId,
      rootRef,
      searchRef,
      open,
      search,
      liveMessage,
      visibleCount,
      selectedOrgUID,
      selectedLabel,
      hasMultipleOrgs,
      showSearch,
      recentOptions,
      visibleOptions,
      canLoadMore,
      remoteLoading,
      remoteError,
      selectionError,
      toggle,
      openAndFocusFirst,
      onOptionKeydown,
      close,
      closeAndRestoreFocus,
      select,
      loadMore,
      loadRemote,
    };
  },
};
</script>

<style scoped>
.org-context-picker {
  position: relative;
  min-width: min(260px, 100%);
}

.org-context-trigger {
  display: flex;
  width: 100%;
  min-height: var(--control-height-compact);
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
}

.org-context-trigger-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.org-context-panel {
  position: absolute;
  z-index: 20;
  top: calc(100% + var(--space-2));
  left: 0;
  width: min(320px, calc(100vw - (2 * var(--space-5))));
  padding: var(--space-4);
  border-radius: var(--radius-sm);
  background: var(--color-surface-raised);
  box-shadow: var(--shadow-surface-raised);
}

/* Same compact search field as the table searches (unit-field + smallInput);
   the label stays available to assistive technology only. */
.org-context-search {
  width: 100%;
  box-sizing: border-box;
}

.org-context-visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

.org-context-section-label {
  display: block;
  margin: 0 0 var(--space-2);
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
}

.org-context-list {
  max-height: 280px;
  overflow-y: auto;
  margin-top: var(--space-2);
}

.org-context-option {
  display: block;
  width: 100%;
  min-height: var(--control-height-compact);
  padding: 0 var(--space-4);
  border: 0;
  background: transparent;
  color: inherit;
  text-align: left;
}

.org-context-option:hover,
.org-context-option:focus-visible,
.org-context-option.selected {
  background: var(--color-surface-subtle);
}

.org-context-load-more {
  width: 100%;
  margin-top: var(--space-2);
}

.org-context-empty {
  margin: var(--space-4) 0;
  color: var(--color-text-muted);
}

.org-context-status {
  margin: var(--space-2) 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
}

.org-context-status-error {
  color: var(--color-feedback-danger-foreground);
}

.org-context-live {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  border: 0;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

</style>
